import { useTranslation } from 'next-i18next';

import { galleriesQueries } from 'src/constants/galleries';
import {
    useAddContentToGalleryMutation,
    useCreateGalleryMutation,
    useDeleteGalleryMutation,
    useUpdateGalleryMutation,
} from 'src/hooks/__generated__/queries';
import useAlerts from 'src/hooks/useAlerts';
import useApolloCacheUpdate from 'src/hooks/useApolloCacheUpdate';
import type { GalleryRole, GalleryInviteeInput } from 'src/types/__generated__/graphql';

type CreateGalleryParams = {
    companyId?: string;
    name: string;
    isSecret: boolean;
    contentToAdd?: {
        postId?: string;
        adId?: string;
        newsPostId?: string;
    };
    invitations?: Array<{
        role: GalleryRole;
        invitee: GalleryInviteeInput;
    }>;
};

type UpdateGalleryParams = {
    id: string;
    name: string;
    isSecret: boolean;
};

export const useManageGallery = () => {
    const { t } = useTranslation(['favorites']);
    const { addAlert } = useAlerts();
    const {
        addFavoritePostInCache,
        addFavoriteAdInCache,
        addFavoriteNewsPostInCache,
        incrementNumberOfContentsOnGalleryInCache,
    } = useApolloCacheUpdate();

    const [createGalleryMutation] = useCreateGalleryMutation();
    const [addContentToGalleryMutation] = useAddContentToGalleryMutation();
    const [updateGalleryMutation] = useUpdateGalleryMutation();
    const [deleteGalleryMutation] = useDeleteGalleryMutation();

    const handleCreateGallery = async ({
        name,
        isSecret,
        companyId,
        contentToAdd,
        invitations,
    }: CreateGalleryParams) => {
        const { data } = await createGalleryMutation({
            variables: {
                name,
                isSecret,
                companyId,
                invitations,
            },
            refetchQueries: ['CurrentUser', ...galleriesQueries],
            awaitRefetchQueries: true,
            onCompleted(createGalleryData) {
                if (contentToAdd) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    addContentToGalleryMutation({
                        variables: {
                            galleryId: createGalleryData.createGallery.id,
                            adId: contentToAdd.adId,
                            postId: contentToAdd.postId,
                            newsPostId: contentToAdd.newsPostId,
                        },
                        onCompleted(addContentToGalleryData) {
                            addAlert({
                                type: 'success',
                                message: t('favorites:addedInGalleryName', {
                                    galleryName: addContentToGalleryData.addContentToGallery.name,
                                }),
                            });
                            if (contentToAdd.postId) {
                                addFavoritePostInCache(contentToAdd.postId);
                            }
                            if (contentToAdd.adId) {
                                addFavoriteAdInCache(contentToAdd.adId);
                            }
                            if (contentToAdd.newsPostId) {
                                addFavoriteNewsPostInCache(contentToAdd.newsPostId);
                            }
                            incrementNumberOfContentsOnGalleryInCache({
                                id: addContentToGalleryData.addContentToGallery.id,
                                numberOfContents: addContentToGalleryData.addContentToGallery.numberOfContents,
                            });
                        },
                    });
                }
            },
        });

        return data?.createGallery;
    };

    const handleUpdateGallery = async ({ id, name, isSecret }: UpdateGalleryParams) =>
        updateGalleryMutation({
            variables: {
                id,
                name,
                isSecret,
            },
            refetchQueries: ['CurrentUser', ...galleriesQueries, 'Gallery'],
            awaitRefetchQueries: true,
        });

    const handleDeleteGallery = async (id: string) => {
        await deleteGalleryMutation({
            variables: {
                id,
            },
            refetchQueries: [...galleriesQueries, 'CurrentUser'],
            awaitRefetchQueries: true,
        });
        addAlert({
            type: 'success',
            message: t('favorites:galleryDeletedSuccess'),
        });
    };

    return {
        createGallery: (params: CreateGalleryParams) => handleCreateGallery(params),
        updateGallery: (params: UpdateGalleryParams) => handleUpdateGallery(params),
        deleteGallery: (id: string) => handleDeleteGallery(id),
    };
};
